import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import { apiErrorText } from './apiError';
import { residentKeys } from './residentsApi';

// ===== Types =====

export interface FacilitySet {
  pKey_Facility_Sets: number;
  fKey_Facility: number;
  mealtime: number;
  name: string;
  number: number;
  type: number;
  fKey_LayoutDiningRoom: number | null;
  mealtimeName: string | null;
}

export interface RoomBedTrayLine {
  pKey_RoomBed_TrayLine: number;
  fKey_Unit_Room_Mapping: number;
  set_B: number | null;
  set_L: number | null;
  set_D: number | null;
  tray_B: number | null;
  tray_L: number | null;
  tray_D: number | null;
  bed: string | null;
  unit: string | null;
  floor: string | null;
  wing: string | null;
  room: number | null;
}

export interface UnassignedRoom {
  pKey_Unit_Room_Mapping: number;
  fKey_Unit_Mapping: number;
  room: number;
  beds: string | null;
  unit: string | null;
  floor: string | null;
  wing: string | null;
}

export interface PatientTrayLineItem {
  pKey_Patient: number;
  fullName: string;
  firstName: string;
  lastName: string;
  room_No: string;
  bed_No: string;
  floor: string;
  wing: string;
  unit: string | null;
  set_B: number;
  set_L: number;
  set_D: number;
  tray_B: number;
  tray_L: number;
  tray_D: number;
  b_Location: string | null;
  l_Location: string | null;
  d_Location: string | null;
  tF_Tube_Feed: boolean;
  /**
   * The current diet order includes the facility's NPO diet type
   * (tblFacility.FKey_Diet_TypeNPO). Auto-Assign leaves these out unless
   * "Include NPO" is checked. Optional for older API builds.
   */
  isNPO?: boolean;
  /** Discharged residents keep their place in a set (as in WinForms). */
  discharged?: boolean;
  /** The Diet Order column — diet types then consistencies, ", "-joined. */
  dietOrder?: string;
}

/** Another resident's per-day override (tblPatient_TrayLineOverride). */
export interface FacilityTrayLineOverride {
  fKey_Patient: number;
  fullName: string;
  /** Monday 1 .. Sunday 7. */
  day: number;
  /** 0 B, 1 L, 2 D. */
  mealtime: number;
  trayLineSet: number;
  trayLineTray: number;
}

/** The distinct tray ticket locations in use, per meal. */
export interface TrayLineLocations {
  b: string[];
  l: string[];
  d: string[];
}

export interface PatientTrayLineUpdate {
  pKey_Patient: number;
  set_B: number;
  set_L: number;
  set_D: number;
  tray_B: number;
  tray_L: number;
  tray_D: number;
  b_Location: string | null;
  l_Location: string | null;
  d_Location: string | null;
}

// ===== Query Keys =====

export const trayLineKeys = {
  all: ['trayLine'] as const,
  facilitySets: (facilityId: number) => [...trayLineKeys.all, 'facilitySets', facilityId] as const,
  facilitySetsByMeal: (facilityId: number, mealtime: number) => [...trayLineKeys.all, 'facilitySets', facilityId, mealtime] as const,
  roomBed: (facilityId: number) => [...trayLineKeys.all, 'roomBed', facilityId] as const,
  unassigned: (facilityId: number, mealtime: number, setNumber: number) => [...trayLineKeys.all, 'unassigned', facilityId, mealtime, setNumber] as const,
  patients: (facilityId: number) => [...trayLineKeys.all, 'patients', facilityId] as const,
  overrides: (facilityId: number) => [...trayLineKeys.all, 'overrides', facilityId] as const,
  locations: (facilityId: number) => [...trayLineKeys.all, 'locations', facilityId] as const,
};

// ===== Query Hooks =====

export function useFacilitySets(facilityId: number) {
  return useQuery({
    queryKey: trayLineKeys.facilitySets(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<FacilitySet[]>(API.facilitySets(facilityId));
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

export function useRoomBedTrayLines(facilityId: number) {
  return useQuery({
    queryKey: trayLineKeys.roomBed(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<RoomBedTrayLine[]>(API.trayLineRoomBed(facilityId));
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

// The per-action Room/Bed hooks (useUnassignedRooms, useSaveTrayLines,
// useDeleteTrayLine, useUnassignTrayLine, useAutoAssignTrayNumbers,
// useCopyTrayLine) had no callers once the Room/Bed page moved to the
// working-set Save, and their API endpoints are gone (tray line review F-11).

// ===== Patient Tray Line Hooks =====

export function usePatientsForTrayLine(facilityId: number) {
  return useQuery({
    queryKey: trayLineKeys.patients(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<PatientTrayLineItem[]>(API.trayLinePatients(facilityId));
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

/** Every resident's per-day overrides, for the "Already in use" check on the Tray Line tab. */
export function useFacilityTrayLineOverrides(facilityId: number) {
  return useQuery({
    queryKey: trayLineKeys.overrides(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<FacilityTrayLineOverride[]>(API.trayLineOverrides(facilityId));
      return data;
    },
    staleTime: 60 * 1000,
    enabled: facilityId > 0,
  });
}

/** The locations already in use, for the Assign dialog's Location lists. */
export function useTrayLineLocations(facilityId: number) {
  return useQuery({
    queryKey: trayLineKeys.locations(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<TrayLineLocations>(API.trayLineLocations(facilityId));
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

export function useSavePatientTrayLineAssignments(facilityId: number) {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  return useMutation({
    mutationFn: async (updates: PatientTrayLineUpdate[]) => {
      await apiClient.post(API.trayLineSavePatientAssignments(facilityId), updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trayLineKeys.patients(facilityId) });
      // Writes tblPatient Tray_B/L/D, which drive the residents'
      // "not assigned to a tray line" flag (#2080).
      queryClient.invalidateQueries({ queryKey: residentKeys.flags(facilityId) });
      addNotification('Patient tray line assignments saved', 'success');
    },
    onError: (error: Error) => {
      addNotification(`Error saving patient assignments: ${error.message}`, 'error');
    },
  });
}

// ===== Room/Bed working set (ucDietech_RoomBedTrayLine_DataGrid.vb) =====

/** A row of the working table — `RoomBedTrayLine` plus its unit key. */
export interface RoomBedWorkingRow extends RoomBedTrayLine {
  /** Negative until Save: a row seeded from the unit mapping (.vb:243-304). */
  pKey_Unit_Mapping: number | null;
}

/** The "Unassigned Unit" picker (`Fill_dtUnits`, .vb:877-924). */
export interface RoomBedTrayLineUnit {
  pKey_Unit_Mapping: number;
  unit: string | null;
  floor: string | null;
  wing: string | null;
  floorNo: number;
  wingNo: number;
}

export interface RoomBedWorkingSet {
  rows: RoomBedWorkingRow[];
  units: RoomBedTrayLineUnit[];
  seededCount: number;
}

export interface RoomBedSaveResult {
  inserted: number;
  updated: number;
  deleted: number;
  /** Placeholder key → the real key the insert produced. */
  insertedKeys: Record<string, number>;
  /** New rows refused: their room/bed is not one of this facility's mappings. */
  rejected?: number;
}

export const roomBedWorkingSetKey = (facilityId: number) =>
  [...trayLineKeys.all, 'roomBedWorkingSet', facilityId] as const;

/**
 * The whole table, already cleaned up, renumbered and seeded by the server.
 *
 * Never cached: WinForms re-runs `Init` on every open, and a sibling session
 * editing the same facility would otherwise be invisible until a refetch.
 */
export function useRoomBedWorkingSet(facilityId: number) {
  /** First fetch is the `Init` equivalent and logs View; refetches do not. */
  const loadedOnce = useRef(false);

  return useQuery({
    queryKey: roomBedWorkingSetKey(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<RoomBedWorkingSet>(
        API.trayLineRoomBedWorkingSet(facilityId, !loadedOnce.current),
      );
      loadedOnce.current = true;
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    // Only mount, Refresh and the post-save re-read load it. A refetch on
    // window focus or reconnect handed the page a new table and wiped the
    // unsaved working copy (tray line review F-6).
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: facilityId > 0,
  });
}

/** What a changed row held when the page loaded it (the server's conflict check). */
export interface TrayLineRowOriginal {
  pKey: number;
  set_B: number | null;
  set_L: number | null;
  set_D: number | null;
  tray_B: number | null;
  tray_L: number | null;
  tray_D: number | null;
}

/** The Room/Bed Save body: only the changed rows, their originals and the lock. */
export interface RoomBedSaveRequest {
  rows: RoomBedWorkingRow[];
  originals: TrayLineRowOriginal[];
  lockKey: number | null;
}

/**
 * A failed tray line save, in words. A 409 is the server refusing on purpose
 * (the lock was lost, or somebody else changed the rows) and its text says
 * so; anything else is "Error saving tray line: ...".
 */
export function trayLineSaveErrorText(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const why = apiErrorText(error, (error as Error)?.message ?? 'unknown error');
  return status === 409 ? why : `Error saving tray line: ${why}`;
}

/**
 * The Save button — the changed rows in one request
 * (`_daRoomBedTray.Update(_dtRoomBedTray)`, .vb:1273). Only what changed is
 * sent: posting the whole table let one session revert every row another
 * session had saved since it loaded (tray line review F-1).
 *
 * No `invalidateQueries`: the caller holds the working copy and reconciles
 * the returned keys in place, so a refetch mid-edit cannot wipe it.
 */
export function useSaveRoomBedWorkingSet(facilityId: number) {
  const addNotification = useNotificationStore((s) => s.addNotification);

  return useMutation({
    mutationFn: async (payload: RoomBedSaveRequest) => {
      const { data } = await apiClient.post<RoomBedSaveResult>(
        API.trayLineRoomBedSave(facilityId), payload,
      );
      return data;
    },
    onError: (error: Error) => {
      addNotification(trayLineSaveErrorText(error), 'error');
    },
  });
}

// ===== Resident Assignment working set (ucDietech_TrayLine_DataGrid.vb) =====

/**
 * One entry of the page's change log — `AddChangeLog` (.vb:1636):
 * `"{method} {BLD} ({set},{tray})"`. The page accumulates these as the
 * operator works and sends them with the save, where the server turns them
 * into one interaction note per resident.
 */
export interface PatientTrayLineChange {
  fKey_Patient: number;
  change: string;
}

export interface PatientTrayLineWorkingSet {
  rows: PatientTrayLineItem[];
  locations: TrayLineLocations;
  /** Whether the Room/Bed tab has any assignments (.vb:139-145). */
  autoAssignConfigured: boolean;
}

export interface PatientTrayLineSaveResult {
  updated: number;
  /** Rows the post-save re-sequence moved. */
  resequenced: number;
}

export const residentTrayLineKey = (facilityId: number) =>
  [...trayLineKeys.all, 'residentWorkingSet', facilityId] as const;

/**
 * The whole working set. Never cached: WinForms re-runs `Init` on every
 * open and forces a full resident refresh first (.vb:98).
 */
export function useResidentTrayLineWorkingSet(facilityId: number) {
  /** First fetch is the `Init` equivalent and logs View; refetches do not. */
  const loadedOnce = useRef(false);

  return useQuery({
    queryKey: residentTrayLineKey(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<PatientTrayLineWorkingSet>(
        API.trayLineResidentWorkingSet(facilityId, !loadedOnce.current),
      );
      loadedOnce.current = true;
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    // See useRoomBedWorkingSet: no focus/reconnect refetch (review F-6).
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: facilityId > 0,
  });
}

/**
 * The Save button. The caller holds the working copy and reconciles in
 * place, so there is no `invalidateQueries` to race a mid-edit refetch.
 * `originals` are the changed residents as loaded (the server's conflict
 * check) and `lockKey` the facility lock the page holds.
 */
export function useSaveResidentTrayLine(facilityId: number) {
  const addNotification = useNotificationStore((s) => s.addNotification);

  return useMutation({
    mutationFn: async (payload: {
      rows: PatientTrayLineUpdate[];
      changes: PatientTrayLineChange[];
      originals: PatientTrayLineUpdate[];
      lockKey: number | null;
    }) => {
      const { data } = await apiClient.post<PatientTrayLineSaveResult>(
        API.trayLineResidentSave(facilityId), payload,
      );
      return data;
    },
    onError: (error: Error) => {
      addNotification(trayLineSaveErrorText(error), 'error');
    },
  });
}
